import { parse } from 'date-fns'
import { RouteSnapshotTravelerShipment } from '../components/RouteSnapshot/RouteSnapshot'

export interface TransformLegOptions {
  shipper?: Shipper
  isUserAuthenticated?: boolean
}

export interface RouteSnapshotData {
  type: 'outbound' | 'return'
  shipper: Shipper
  origin: OrderAddress
  destination: OrderAddress
  shipments: RouteSnapshotTravelerShipment[]
  pickup: boolean
  uberPickup?: boolean
  pickupReadyTime?: string
  pickupCloseTime?: string
}

/**
 * Core utility to transform OrderLeg data to RouteSnapshot data structure
 */
export const transformLegToRouteSnapshotData = (
  leg: OrderLeg,
  options: TransformLegOptions = {},
): RouteSnapshotData => {
  // Extract shipper from first shipment if not provided
  const defaultShipper =
    leg.travelers?.[0]?.shipments?.[0]?.carrier_service_level?.carrier?.code ||
    'fedex'
  const shipper = (options.shipper || defaultShipper) as Shipper

  // Transform travelers and their shipments
  const shipments: RouteSnapshotTravelerShipment[] =
    leg.travelers?.map((traveler) => ({
      traveler: traveler.name,
      shipments: traveler.shipments || [],
    })) || []

  // Determine leg type based on leg_index or leg_name
  const type: LegType = getLegType(leg)

  return {
    type,
    shipper,
    origin: leg.origin || ({} as OrderAddress),
    destination: leg.destination || ({} as OrderAddress),
    shipments,
    pickup: leg.pickup || false,
    uberPickup: leg.uber_pickup,
    pickupReadyTime: leg.pickup_ready_time,
    pickupCloseTime: leg.pickup_close_time,
  }
}

/**
 * Helper to get all shipments from a leg (flattened)
 */
export const getAllShipmentsFromLeg = (leg: OrderLeg): OrderShipment[] => {
  return leg.travelers?.flatMap((traveler) => traveler.shipments || []) || []
}

/**
 * Helper to get traveler-shipment mapping from a leg
 */
export const getTravelerShipmentsFromLeg = (
  leg: OrderLeg,
): RouteSnapshotTravelerShipment[] => {
  return (
    leg.travelers?.map((traveler) => ({
      traveler: traveler.name,
      shipments: traveler.shipments || [],
    })) || []
  )
}

/**
 * Helper to determine leg type from OrderLeg
 */
export const getLegType = (leg: OrderLeg): LegType => {
  return leg.leg_index === 1 ? 'outbound' : 'return'
}

/**
 * Helper to extract shipper from leg data
 */
export const getShipperFromLeg = (
  leg: OrderLeg,
  fallback: Shipper = 'fedex',
): Shipper => {
  const firstShipment = leg.travelers?.[0]?.shipments?.[0]
  return (firstShipment?.carrier_service_level?.carrier?.code ||
    fallback) as Shipper
}

// Parse dates from MM/DD/YYYY format
export const parseDate = (dateString: string): Date => {
  if (!dateString) return new Date()

  let d: Date
  try {
    // OrderShipment uses MM/DD/YYYY format according to the interface
    if (dateString.includes('/')) {
      d = parse(dateString, 'MM/dd/yyyy', new Date())
    } else {
      // Fallback to ISO string parsing
      d = new Date(dateString)
    }
  } catch {
    console.warn(`Unable to parse date: ${dateString}`)
    return new Date()
  }

  // Explicit invalid-date check
  if (isNaN(d.getTime())) {
    console.warn(`Unable to parse date: ${dateString}`)
    return new Date()
  }

  return d
}

const ACTIVE_SHIPMENT_STATES = ['shipped', 'in_transit']

export function computeIsDelayed(
  state: string | undefined,
  deliveryDate: Date,
): boolean {
  if (ACTIVE_SHIPMENT_STATES.includes(state?.toLowerCase() ?? '')) return false

  const dayAfterDelivery = new Date(deliveryDate)
  dayAfterDelivery.setDate(dayAfterDelivery.getDate() + 1)
  dayAfterDelivery.setHours(0, 0, 0, 0)

  return new Date() >= dayAfterDelivery && state?.toLowerCase() !== 'delivered'
}

// Map shipment state to delivery steps
export const getDeliveryStatus = (state: string): string => {
  if (!state) return 'Order created'

  const lowerState = state.toLowerCase()
  if (lowerState.includes('delivered') || lowerState.includes('complete')) {
    return 'Delivered'
  }
  if (lowerState.includes('ship') || lowerState.includes('transit')) {
    return 'Shipped'
  }
  return 'Order created'
}

export const generateSnapshotShipmentLeg = (
  legType: LegType,
  origin: Partial<OrderAddress>,
  destination: Partial<OrderAddress>,
  shipments: RouteSnapshotTravelerShipment[],
  pickup?: boolean,
): OrderLeg => {
  return {
    leg_id: legType,
    leg_index: legType === 'outbound' ? 1 : 2,
    leg_type: legType,
    origin: origin as OrderAddress,
    destination: destination as OrderAddress,
    leg_name: legType == 'outbound' ? 'Outbound' : 'Return',
    pickup,
    travelers: shipments.map(({ traveler, shipments }) => ({
      id: '',
      email: '',
      phone: '',
      name: traveler,
      shipments,
    })),
  }
}

export const pickupDropoffText = (pickup?: boolean) => {
  return pickup ? 'pick up' : 'drop off'
}
